var _react_refresh_temp_1;
var _react_refresh_temp_2;
_react_refresh_temp_2 = $RefreshSig$();
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useImperativeHandle, useRef, useState, } from 'react';
import { ARROW_LEFT_KEY, ARROW_RIGHT_KEY, BACKSPACE_KEY, COMMA_KEY, DELETE_KEY, ENTER_KEY, PASSWORD_MANAGER_BLOCKING_CLASS_NAMES, PASSWORD_MANAGER_BLOCKING_HTML_ATTRIBUTES, SPACE_KEY, } from '#constants';
import { useOnFocusout } from '#hooks';
import { C } from '#integrations';
import { getDescriptionId, mergeAriaIds } from '#services';
import { InfoCircleIcon } from '../../icons';
import { Tag } from '../../Tag';
import { Tooltip, useTooltip } from '../../Tooltip';
import { Typography } from '../../Typography';
import { useDescribedBy } from '../components/DescribedByContext';
import { TagInputTestIdFactory } from './interactions';
import styles from './TagInput.module.css';
export const TagInput = ({ 'aria-describedby': ariaDescribedBy, 'aria-errormessage': ariaErrorMessage, 'aria-invalid': ariaInvalid, 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy, autoComplete, 'data-testid': testid, description, disabled, getTagErrorText, hasErrors, id, label, labelTooltip, onBlur, onChange, onFocus, placeholder, ref: forwardedRef, required, shouldDisablePasswordManagers, tagListClassName, tooltipPosition, validatorErrors, value, }) => {
    _react_refresh_temp_2();
    const [isFocused, setIsFocused] = useState(false);
    const [currentSelectedTag, setCurrentSelectedTag] = useState(-1);
    // innerRef is required for the focus effects on the border and also
    // for the input to work without a passed ref from outside.
    // The input element in this component is not controlled - if we control it
    // it causes huge rerender chain which causes lag
    const innerRef = useRef(null);
    const containerRef = useRef(null);
    const descriptionId = getDescriptionId(id);
    const wrapperDescriptionId = useDescribedBy();
    const describedBy = mergeAriaIds(description ? descriptionId : undefined, wrapperDescriptionId, ariaDescribedBy);
    // Setting the forwardedRef to our innerRef here. Using useImperativeHandle, because every other way
    // needs typecasting everywhere and still throws TS errors
    // This bang '!' operator is... needed because of React's very interesting
    // way of doing refs and having legacy ref etc..
    useImperativeHandle(forwardedRef, () => innerRef.current, []);
    const { anchorRef, isTooltipActive, tooltipProps, triggerProps } = useTooltip({
        placement: tooltipPosition,
    });
    const setValueFromInput = useCallback(() => {
        if (!innerRef.current) {
            return;
        }
        const trimmedInputValue = innerRef.current.value.trim();
        if (trimmedInputValue.length === 0) {
            return;
        }
        // Iterating through the value array once is way faster that creating new Set
        if (value.find((entry) => entry === trimmedInputValue)) {
            innerRef.current.value = '';
            return;
        }
        else {
            onChange([...value, trimmedInputValue]);
            innerRef.current.value = '';
        }
    }, [onChange, value]);
    const resetCurrentSelectedTag = useCallback(() => {
        setCurrentSelectedTag(-1);
    }, []);
    const onInputKeyDownHandler = useCallback((e) => {
        switch (e.key) {
            case COMMA_KEY:
            case ENTER_KEY:
            case SPACE_KEY: {
                e.preventDefault();
                e.stopPropagation();
                setValueFromInput();
                break;
            }
            case BACKSPACE_KEY: {
                if (value.length && !innerRef.current?.value) {
                    e.preventDefault();
                    const newValue = currentSelectedTag === -1
                        ? value.slice(0, value.length - 1)
                        : value
                            .slice(0, currentSelectedTag)
                            .concat(value.slice(currentSelectedTag + 1));
                    if (newValue.length === 0) {
                        resetCurrentSelectedTag();
                    }
                    else {
                        if (currentSelectedTag >= newValue.length) {
                            setCurrentSelectedTag(newValue.length - 1);
                        }
                    }
                    onChange(newValue);
                }
                break;
            }
            case DELETE_KEY: {
                if (value.length && currentSelectedTag !== -1) {
                    e.preventDefault();
                    const newValue = value
                        .slice(0, currentSelectedTag)
                        .concat(value.slice(currentSelectedTag + 1));
                    if (newValue.length === 0) {
                        resetCurrentSelectedTag();
                    }
                    else {
                        if (currentSelectedTag >= newValue.length) {
                            setCurrentSelectedTag(newValue.length - 1);
                        }
                    }
                    onChange(newValue);
                }
                break;
            }
            case ARROW_RIGHT_KEY: {
                if (value.length && currentSelectedTag !== -1) {
                    e.preventDefault();
                    const nextTag = currentSelectedTag >= value.length - 1
                        ? -1
                        : currentSelectedTag + 1;
                    setCurrentSelectedTag(nextTag);
                }
                break;
            }
            case ARROW_LEFT_KEY: {
                if (value.length &&
                    (currentSelectedTag !== -1 ||
                        !innerRef.current?.value)) {
                    e.preventDefault();
                    const prevTag = currentSelectedTag === -1
                        ? value.length - 1
                        : currentSelectedTag - 1;
                    if (prevTag >= 0) {
                        setCurrentSelectedTag(prevTag);
                    }
                }
                break;
            }
        }
    }, [
        currentSelectedTag,
        onChange,
        resetCurrentSelectedTag,
        setValueFromInput,
        value,
    ]);
    const onInputPasteHandler = useCallback((e) => {
        e.preventDefault();
        if (!e.clipboardData) {
            return;
        }
        const pastedData = e.clipboardData
            .getData('text/plain')
            .split(',')
            // Trim is needed as copy/pasting might include white space or new lines
            .map((pastedString) => pastedString.trim());
        // Order might not be kept if we just use a Set and add to it, so we use the set only to guarantee
        // uniqueness of data and we spread the 2 arrays to ensure proper order.
        const currentData = new Set(value);
        const newValues = [];
        pastedData.forEach((entry) => {
            if (entry.length > 0 && !currentData.has(entry)) {
                newValues.push(entry);
                currentData.add(entry);
            }
        });
        onChange([...value, ...newValues]);
    }, [onChange, value]);
    useOnFocusout({
        callback: setValueFromInput,
        element: containerRef,
    });
    return (_jsxs("div", { className: styles.Container, "data-testid": TagInputTestIdFactory.getTagInputContainerTestId(testid), ref: containerRef, children: [Boolean(label) && (_jsxs("span", { ...(labelTooltip && triggerProps), className: styles.LabelContainer, children: [_jsx(Typography, { className: C(disabled && styles.LabelDisabled), color: "accent", "data-testid": TagInputTestIdFactory.getTagInputLabelTestId(testid), htmlFor: id, kind: "label", children: label }), labelTooltip && (_jsxs(_Fragment, { children: [_jsx(InfoCircleIcon, { ref: anchorRef, size: "small" }), isTooltipActive && (_jsx(Tooltip, { contents: labelTooltip, tooltipProps: tooltipProps }))] }))] })), Boolean(description) && (_jsx(Typography, { className: C(disabled && styles.DescriptionDisabled), "data-testid": TagInputTestIdFactory.getTagInputDescriptionTestId(testid), id: descriptionId, kind: "generic-small", children: description })), _jsxs("div", { className: C(styles.InnerWrapper, disabled && styles.Disabled, isFocused && styles.Focused, hasErrors && styles.Errored, tagListClassName), onClick: () => {
                    setIsFocused(true);
                    innerRef.current?.focus();
                }, children: [value.length > 0 && (_jsx("ol", { className: styles.Listbox, ...(value.length > 0
                            ? {
                                role: 'listbox',
                                // TODO(LUCID-35065): hardcoded English label; take it as a prop from the app.
                                'aria-label': 'selected values',
                                ...(disabled && {
                                    'aria-disabled': true,
                                }),
                            }
                            : {}), children: value.map((entry, index) => {
                            // Each entry from value will be revalidated on each re-render
                            const error = validatorErrors[entry];
                            return (_jsx("li", { className: styles.TagLi, role: "none", children: _jsx(Tag, { "data-testid": TagInputTestIdFactory.internalGenerateTagTestId(testid, entry), disabled: disabled, hasError: Boolean(error), isFocused: currentSelectedTag === index, onClear: (event) => {
                                        event.stopPropagation();
                                        onChange(value.filter((arrayEntry) => arrayEntry !== entry));
                                        if (innerRef.current) {
                                            innerRef.current.focus();
                                        }
                                    }, onClick: (event) => {
                                        event.stopPropagation();
                                        setCurrentSelectedTag(index);
                                        if (innerRef.current) {
                                            innerRef.current.focus();
                                        }
                                    }, ...(error && {
                                        tooltipContent: error.map(getTagErrorText),
                                    }), value: entry }) }, entry));
                        }) })), _jsx("input", { ...(describedBy && {
                            'aria-describedby': describedBy,
                        }), ...(ariaErrorMessage && {
                            'aria-errormessage': ariaErrorMessage,
                        }), ...(ariaInvalid && { 'aria-invalid': true }), "aria-label": ariaLabel, "aria-labelledby": ariaLabelledBy, ...(required && { 'aria-required': true }), autoCapitalize: "off", autoComplete: autoComplete, className: C(styles.Input, shouldDisablePasswordManagers &&
                            PASSWORD_MANAGER_BLOCKING_CLASS_NAMES), "data-testid": TagInputTestIdFactory.getTagInputInputTestId(testid), disabled: disabled, id: id, ref: innerRef, ...(value.length === 0 && {
                            placeholder,
                        }), onBlur: (e) => {
                            setIsFocused(false);
                            resetCurrentSelectedTag();
                            if (onBlur) {
                                onBlur(e);
                            }
                        }, onFocus: (e) => {
                            setIsFocused(true);
                            if (onFocus) {
                                onFocus(e);
                            }
                        }, onKeyDown: onInputKeyDownHandler, onPaste: onInputPasteHandler, type: "text", ...(shouldDisablePasswordManagers
                            ? PASSWORD_MANAGER_BLOCKING_HTML_ATTRIBUTES
                            : {}) })] })] }));
};
_react_refresh_temp_1 = TagInput;
$RefreshReg$(_react_refresh_temp_1, "TagInput");
_react_refresh_temp_2(TagInput, "tHFzLUSG+ELt+4v45sJ/9q+9jBM=", false, () => [useDescribedBy, useTooltip, useOnFocusout]);
